import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RenderJob } from '../render-flow.model';
import { RENDER_FEEDBACK_IMPROVE, RenderFeedbackRating, RenderFeedbackService, RenderFeedbackStage } from '../render-feedback.service';
import { RenderOrientation } from '../render.data';

export interface RenderFeedbackDialogData {
  job: RenderJob;
  stage: RenderFeedbackStage;
  // 'dislike' from the preview's thumb: the dislike is the message, so the form may go empty.
  rating: RenderFeedbackRating | null;
  // The frame whose thumb opened it; none from the line under the videos.
  orientation?: RenderOrientation;
  userName: string;
}

// Feedback on a model's preview or videos (designer, Figma 28753:46113, 2026-10-21; it was frame
// Account_web, 28734:45789): what could be better, a box. Opened by the
// preview's thumbs-down (render-vote) — Send is live from the start, an empty form sends the
// dislike alone (owner) — and by the line under the videos (render-rating), where Send wakes up
// with the first chip or word. Closes with 'sent' once it went through; Back, Esc or a click
// beside it close it with nothing. The thank-you waits for Close (designer, 2026-10-16).
@Component({
  selector: 'app-render-feedback-dialog',
  templateUrl: './render-feedback-dialog.component.html',
  styleUrls: ['./render-feedback-dialog.component.scss'],
})
export class RenderFeedbackDialogComponent {
  public readonly improve = RENDER_FEEDBACK_IMPROVE;
  public text = '';
  public sending = false;
  public sent = false;
  public error = '';
  private readonly chosen = new Set<string>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: RenderFeedbackDialogData,
    private readonly dialogRef: MatDialogRef<RenderFeedbackDialogComponent, 'sent' | undefined>,
    private readonly feedback: RenderFeedbackService,
  ) {}

  // Send is gone with the form: the keyboard lands on Close.
  @ViewChild('doneButton')
  public set doneButton(button: ElementRef<HTMLButtonElement> | undefined) {
    button?.nativeElement.focus();
  }

  public get canSend(): boolean {
    if (this.sending) {
      return false;
    }
    return this.data.rating === 'dislike' || this.chosen.size > 0 || !!this.text.trim();
  }

  public isOn(id: string): boolean {
    return this.chosen.has(id);
  }

  public toggle(id: string): void {
    if (!this.chosen.delete(id)) {
      this.chosen.add(id);
    }
    this.error = '';
  }

  public onBack(): void {
    this.dialogRef.close();
  }

  public async onSend(): Promise<void> {
    if (!this.canSend) {
      return;
    }
    this.sending = true;
    this.error = '';
    try {
      await this.feedback.send(this.data.job, {
        stage: this.data.stage,
        rating: this.data.rating,
        improve: this.improve.filter((chip) => this.chosen.has(chip.id)).map((chip) => chip.id),
        orientation: this.data.orientation || null,
        text: this.text.trim(),
      });
      this.sent = true;
    } catch {
      this.error = 'It did not go through. Please try again.';
    } finally {
      this.sending = false;
    }
  }

  public onDone(): void {
    this.dialogRef.close('sent');
  }
}
